import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { HelpOutline as HelpIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * UI-CONSISTENCY.1 — the ONE venue page header.
 *
 * Standardizes every venue-tab page on: [backLink /] icon + h5/700 title
 * (+ count caption) (+ HelpOutline help) …actions right-aligned, with an
 * optional body2 subtitle line beneath. Replaces the drifted h6/600 headers,
 * in-Paper headers, and InfoOutlined help buttons.
 *
 * Pages render it as the first child of the standard page root
 * (`<Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>`),
 * so it carries no outer margins of its own.
 */

interface PageHeaderProps {
  /** Icon element; rendered in primary.main. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  /** Optional count shown as "(n)" after the title. */
  count?: number;
  /** body2 text.secondary line under the title row. */
  subtitle?: React.ReactNode;
  /** Breadcrumb-style back affordance before the icon (e.g. Alerts → Inventory). */
  backLink?: { label: string; to: string };
  /** Opens the page's help modal; renders the standard HelpOutline button. */
  onHelp?: () => void;
  /** Tooltip for the help button (or the full help content for tooltip-only help). */
  helpTitle?: React.ReactNode;
  /** Right-aligned action cluster (buttons, toggles). */
  actions?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  icon, title, count, subtitle, backLink, onHelp, helpTitle, actions,
}) => {
  const theme = useTheme();

  const helpButton = (onHelp || helpTitle) && (
    <Tooltip title={helpTitle ?? 'About this page'}>
      <IconButton
        size="small"
        onClick={onHelp}
        sx={{ color: theme.palette.text.secondary }}
        aria-label="Help"
      >
        <HelpIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {backLink && (
          <>
            <Button
              component={RouterLink}
              to={backLink.to}
              startIcon={<BackIcon />}
              sx={{ textTransform: 'none', color: theme.palette.text.secondary, pl: 0, minWidth: 0 }}
            >
              {backLink.label}
            </Button>
            <Typography variant="body2" color="text.disabled">/</Typography>
          </>
        )}
        {icon && <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{icon}</Box>}
        <Typography variant="h5" fontWeight={700}>{title}</Typography>
        {count !== undefined && (
          <Typography variant="caption" color="text.secondary">({count})</Typography>
        )}
        {helpButton}
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
            {actions}
          </Box>
        )}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default PageHeader;
